import { useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Mail, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Single-panel auth flow for email + password login next to Google OAuth.
 * Mode ladder: signin ↔ register → verify → (in) · signin → forgot → reset → (in)
 */
export default function EmailAuthPanel({ onGoogleLogin, defaultMode = "signin" }) {
  const { t } = useT();
  const { setUser } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", password2: "", code: "", newPassword: "" });
  const [pendingEmail, setPendingEmail] = useState("");

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const detail = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && d.message) return d.message;
    return fallback || t("emailAuthGeneric");
  };

  const handleGoogle = () => onGoogleLogin?.();

  const doRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) {
      toast.error(t("emailAuthMismatch"));
      return;
    }
    setBusy(true);
    try {
      // ?src=friend&ref=<code> on the landing URL comes from the "Aanbevelen"
      // flow — carry both forward so the backend can grant the SENDER
      // +14 trial days when this new user later converts to a paid pack.
      const params = new URLSearchParams(window.location.search);
      const referral_source = (params.get("src") || "").toLowerCase();
      const referrer_ref_code = (params.get("ref") || "").trim();
      const payload = { email: form.email.trim(), password: form.password };
      if (referral_source) payload.referral_source = referral_source;
      if (referrer_ref_code) payload.referrer_ref_code = referrer_ref_code;
      const { data } = await api.post("/auth/register", payload);
      setPendingEmail(data.email);
      toast.success(t("emailAuthCodeSent"));
      setMode("verify");
    } catch (err) {
      toast.error(detail(err, t("emailAuthRegisterFailed")));
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", {
        email: pendingEmail || form.email.trim(),
        code: form.code.trim(),
      });
      setUser(data.user);
      toast.success(t("emailAuthWelcome"));
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(detail(err, t("emailAuthVerifyFailed")));
    } finally {
      setBusy(false);
    }
  };

  const doResend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-code", { email: pendingEmail || form.email.trim() });
      toast.success(t("emailAuthCodeResent"));
    } catch {
      // silent — endpoint always 200s on purpose
    } finally {
      setBusy(false);
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email.trim(), password: form.password });
      setUser(data.user);
      window.location.href = "/dashboard";
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "email_unverified") {
        setPendingEmail(d.email || form.email.trim());
        toast.info(d.message || t("emailAuthNeedsVerify"));
        setMode("verify");
        return;
      }
      toast.error(detail(err, t("emailAuthLoginFailed")));
    } finally {
      setBusy(false);
    }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email.trim() });
      setPendingEmail(form.email.trim());
      toast.success(t("emailAuthResetSent"));
      setMode("reset");
    } catch (err) {
      toast.error(detail(err, t("emailAuthGeneric")));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        email: pendingEmail || form.email.trim(),
        code: form.code.trim(),
        new_password: form.newPassword,
      });
      setUser(data.user);
      toast.success(t("emailAuthResetOk"));
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(detail(err, t("emailAuthResetFailed")));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-black transition-colors";
  const labelCls = "block ed-label mb-1.5";
  const primaryBtnCls = "w-full inline-flex items-center justify-center gap-2 bg-[#24211D] text-[#F9F8F6] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#3D3934] transition-colors disabled:opacity-60";
  const linkCls = "font-mono text-[0.7rem] uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 underline underline-offset-2";
  const googleLabel = mode === "register" ? t("loginGoogleRegister") : t("loginGoogleSignin");

  return (
    <div className="w-full max-w-md" data-testid="email-auth-panel">
      {/* Google — prominent on top */}
      <button
        data-testid="email-auth-google"
        onClick={handleGoogle}
        className="w-full inline-flex items-center justify-center gap-3 bg-[#24211D] text-[#F9F8F6] px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#3D3934] transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.27-.97 2.35-2.06 3.07l3.33 2.58c1.94-1.79 3.06-4.42 3.06-7.54 0-.75-.07-1.47-.2-2.16H12z" />
          <path fill="#34A853" d="M6.62 14.29l-.76.58-2.7 2.1C4.85 20.09 8.15 22 12 22c2.7 0 4.97-.9 6.62-2.44l-3.33-2.58c-.9.61-2.05.97-3.29.97-2.53 0-4.68-1.7-5.45-4.02z" />
          <path fill="#FBBC05" d="M3.17 7.03A9.98 9.98 0 002 12c0 1.6.38 3.12 1.06 4.47l3.53-2.76c-.18-.55-.28-1.13-.28-1.71 0-.58.1-1.16.28-1.71L3.17 7.03z" />
          <path fill="#4285F4" d="M12 5.86c1.47 0 2.79.5 3.83 1.5l2.87-2.86C16.96 2.9 14.7 2 12 2 8.15 2 4.85 3.91 3.17 7.03l3.42 2.65C7.36 7.36 9.5 5.86 12 5.86z" />
        </svg>
        {googleLabel}
      </button>

      {/* Divider */}
      <div className="my-6 flex items-center gap-3">
        <span className="flex-1 h-px bg-black/10" />
        <span className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-neutral-500">{t("emailAuthOr")}</span>
        <span className="flex-1 h-px bg-black/10" />
      </div>

      {/* Signin/Register tab-switch — makes the two paths visually
          equal-weight. Previously only "Inloggen" was visible up-front
          and the register-link sat at the bottom of the signin form,
          leading new visitors to believe the panel was for existing
          accounts only. Hidden during verify/forgot/reset sub-flows
          because those are continuations of an already-picked path. */}
      {(mode === "signin" || mode === "register") && (
        <div
          className="grid grid-cols-2 gap-0 border border-black/15 mb-5"
          role="tablist"
          data-testid="email-auth-tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            data-testid="email-auth-tab-register"
            onClick={() => setMode("register")}
            className={`px-4 py-3 font-mono text-[0.7rem] uppercase tracking-[0.15em] transition-colors ${
              mode === "register"
                ? "bg-[#24211D] text-[#F9F8F6]"
                : "bg-white text-neutral-700 hover:bg-neutral-100"
            }`}
          >
            {t("emailAuthTabRegister")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "signin"}
            data-testid="email-auth-tab-signin"
            onClick={() => setMode("signin")}
            className={`px-4 py-3 font-mono text-[0.7rem] uppercase tracking-[0.15em] transition-colors ${
              mode === "signin"
                ? "bg-[#24211D] text-[#F9F8F6]"
                : "bg-white text-neutral-700 hover:bg-neutral-100"
            }`}
          >
            {t("emailAuthTabSignin")}
          </button>
        </div>
      )}

      {/* --- SIGN IN --- */}
      {mode === "signin" && (
        <form onSubmit={doLogin} className="space-y-4" data-testid="email-auth-signin">
          <div>
            <label className={labelCls} htmlFor="signin-email">{t("emailAuthEmail")}</label>
            <input id="signin-email" data-testid="signin-email" type="email" required autoComplete="email"
                   className={inputCls} value={form.email} onChange={(e) => upd({ email: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="signin-password">{t("emailAuthPassword")}</label>
            <input id="signin-password" data-testid="signin-password" type="password" required autoComplete="current-password"
                   className={inputCls} value={form.password} onChange={(e) => upd({ password: e.target.value })} />
          </div>
          <button data-testid="signin-submit" type="submit" disabled={busy} className={primaryBtnCls}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} strokeWidth={1.6} />}
            {t("emailAuthLoginCta")}
            <ArrowRight size={13} strokeWidth={1.5} />
          </button>
          <div className="flex items-center justify-between gap-3 pt-2">
            <button type="button" data-testid="signin-to-forgot" onClick={() => setMode("forgot")} className={linkCls}>
              {t("emailAuthForgotLink")}
            </button>
            <button type="button" data-testid="signin-to-register" onClick={() => setMode("register")} className={linkCls}>
              {t("emailAuthRegisterLink")}
            </button>
          </div>
        </form>
      )}

      {/* --- REGISTER --- */}
      {mode === "register" && (
        <form onSubmit={doRegister} className="space-y-4" data-testid="email-auth-register">
          <div>
            <label className={labelCls} htmlFor="reg-email">{t("emailAuthEmail")}</label>
            <input id="reg-email" data-testid="reg-email" type="email" required autoComplete="email"
                   className={inputCls} value={form.email} onChange={(e) => upd({ email: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="reg-password">{t("emailAuthChoosePassword")}</label>
            <input id="reg-password" data-testid="reg-password" type="password" required minLength={8} autoComplete="new-password"
                   className={inputCls} value={form.password} onChange={(e) => upd({ password: e.target.value })} />
            <p className="mt-1 text-[0.65rem] font-mono text-neutral-500">{t("emailAuthPwHint")}</p>
          </div>
          <div>
            <label className={labelCls} htmlFor="reg-password2">{t("emailAuthConfirmPassword")}</label>
            <input id="reg-password2" data-testid="reg-password2" type="password" required minLength={8} autoComplete="new-password"
                   className={inputCls} value={form.password2} onChange={(e) => upd({ password2: e.target.value })} />
          </div>
          <button data-testid="register-submit" type="submit" disabled={busy} className={primaryBtnCls}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} strokeWidth={1.6} />}
            {t("emailAuthRegisterCta")}
            <ArrowRight size={13} strokeWidth={1.5} />
          </button>
          <div className="pt-1 text-center">
            <button type="button" data-testid="register-to-signin" onClick={() => setMode("signin")} className={linkCls}>
              {t("emailAuthBackToLogin")}
            </button>
          </div>
        </form>
      )}

      {/* --- VERIFY --- */}
      {mode === "verify" && (
        <form onSubmit={doVerify} className="space-y-4" data-testid="email-auth-verify">
          <p className="text-sm text-neutral-700 leading-relaxed">
            {t("emailAuthVerifyIntro")} <span className="font-semibold">{pendingEmail || form.email}</span>
          </p>
          <div>
            <label className={labelCls} htmlFor="verify-code">{t("emailAuthCodeLabel")}</label>
            <input id="verify-code" data-testid="verify-code" type="text" inputMode="numeric" pattern="[0-9]{6}"
                   maxLength={6} required autoComplete="one-time-code"
                   className={`${inputCls} font-mono text-2xl tracking-[0.4em] text-center`}
                   value={form.code} onChange={(e) => upd({ code: e.target.value.replace(/\D/g, "") })} />
          </div>
          <button data-testid="verify-submit" type="submit" disabled={busy || form.code.length !== 6} className={primaryBtnCls}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={1.6} />}
            {t("emailAuthVerifyCta")}
          </button>
          <div className="flex items-center justify-between gap-3 pt-1">
            <button type="button" data-testid="verify-resend" onClick={doResend} disabled={busy} className={linkCls}>
              {t("emailAuthResendLink")}
            </button>
            <button type="button" data-testid="verify-to-signin" onClick={() => setMode("signin")} className={linkCls}>
              {t("emailAuthBackToLogin")}
            </button>
          </div>
        </form>
      )}

      {/* --- FORGOT --- */}
      {mode === "forgot" && (
        <form onSubmit={doForgot} className="space-y-4" data-testid="email-auth-forgot">
          <p className="text-sm text-neutral-700 leading-relaxed">{t("emailAuthForgotIntro")}</p>
          <div>
            <label className={labelCls} htmlFor="forgot-email">{t("emailAuthEmail")}</label>
            <input id="forgot-email" data-testid="forgot-email" type="email" required autoComplete="email"
                   className={inputCls} value={form.email} onChange={(e) => upd({ email: e.target.value })} />
          </div>
          <button data-testid="forgot-submit" type="submit" disabled={busy} className={primaryBtnCls}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} strokeWidth={1.6} />}
            {t("emailAuthForgotCta")}
          </button>
          <div className="pt-1 text-center">
            <button type="button" data-testid="forgot-to-signin" onClick={() => setMode("signin")} className={linkCls}>
              {t("emailAuthBackToLogin")}
            </button>
          </div>
        </form>
      )}

      {/* --- RESET --- */}
      {mode === "reset" && (
        <form onSubmit={doReset} className="space-y-4" data-testid="email-auth-reset">
          <p className="text-sm text-neutral-700 leading-relaxed">
            {t("emailAuthResetIntro")} <span className="font-semibold">{pendingEmail || form.email}</span>
          </p>
          <div>
            <label className={labelCls} htmlFor="reset-code">{t("emailAuthCodeLabel")}</label>
            <input id="reset-code" data-testid="reset-code" type="text" inputMode="numeric" pattern="[0-9]{6}"
                   maxLength={6} required
                   className={`${inputCls} font-mono text-2xl tracking-[0.4em] text-center`}
                   value={form.code} onChange={(e) => upd({ code: e.target.value.replace(/\D/g, "") })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="reset-newpw">{t("emailAuthNewPassword")}</label>
            <input id="reset-newpw" data-testid="reset-newpw" type="password" required minLength={8} autoComplete="new-password"
                   className={inputCls} value={form.newPassword} onChange={(e) => upd({ newPassword: e.target.value })} />
            <p className="mt-1 text-[0.65rem] font-mono text-neutral-500">{t("emailAuthPwHint")}</p>
          </div>
          <button data-testid="reset-submit" type="submit" disabled={busy || form.code.length !== 6} className={primaryBtnCls}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={1.6} />}
            {t("emailAuthResetCta")}
          </button>
          <div className="pt-1 text-center">
            <button type="button" data-testid="reset-to-signin" onClick={() => setMode("signin")} className={linkCls}>
              {t("emailAuthBackToLogin")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
